"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";

export default function FoodLoader({
  size = "md",
  text = "Preparing your order",
  theme = "amber",
  className = "",
}: {
  size?: "sm" | "md" | "lg";
  text?: string;
  theme?: "amber" | "red" | "green" | "blue";
  className?: string;
}) {
  const [progress, setProgress] = useState(0);
  const [isDelivering, setIsDelivering] = useState(false);

  // Theme colors
  const colors = {
    amber: {
      primary: "from-amber-400 to-amber-500",
      secondary: "from-amber-300 to-amber-400",
      dark: "from-amber-700 to-amber-800",
      light: "from-amber-50 to-amber-100",
      text: "text-amber-700",
      progress: "stroke-amber-500",
      progressBg: "stroke-amber-200/30",
    },
    red: {
      primary: "from-red-400 to-red-500",
      secondary: "from-red-300 to-red-400",
      dark: "from-red-700 to-red-800",
      light: "from-red-50 to-red-100",
      text: "text-red-700",
      progress: "stroke-red-500",
      progressBg: "stroke-red-200/30",
    },
    green: {
      primary: "from-emerald-400 to-emerald-500",
      secondary: "from-emerald-300 to-emerald-400",
      dark: "from-emerald-700 to-emerald-800",
      light: "from-emerald-50 to-emerald-100",
      text: "text-emerald-700",
      progress: "stroke-emerald-500",
      progressBg: "stroke-emerald-200/30",
    },
    blue: {
      primary: "from-sky-400 to-sky-500",
      secondary: "from-sky-300 to-sky-400",
      dark: "from-sky-700 to-sky-800",
      light: "from-sky-50 to-sky-100",
      text: "text-sky-700",
      progress: "stroke-sky-500",
      progressBg: "stroke-sky-200/30",
    },
  };

  // Size variants
  const sizes = {
    sm: {
      container: "w-64 h-64",
      plate: "w-24 h-24",
      food: "w-16 h-16",
      vehicle: "w-16 h-16",
      text: "text-sm",
      progressWidth: "3",
    },
    md: {
      container: "w-80 h-80",
      plate: "w-32 h-32",
      food: "w-20 h-20",
      vehicle: "w-20 h-20",
      text: "text-base",
      progressWidth: "4",
    },
    lg: {
      container: "w-96 h-96",
      plate: "w-40 h-40",
      food: "w-24 h-24",
      vehicle: "w-24 h-24",
      text: "text-lg",
      progressWidth: "5",
    },
  };

  // Simulate progress
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + 1;
        if (newProgress >= 100) {
          clearInterval(interval);
          setIsDelivering(true);
          return 100;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div
        className={`${sizes[size].container} relative flex items-center justify-center`}
      >
        {/* Background glow */}
        <motion.div
          className={`absolute inset-0 rounded-full bg-gradient-to-br ${colors[theme].light}/20 blur-xl`}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Number.POSITIVE_INFINITY,
            ease: "easeInOut",
          }}
        />

        {/* Progress circle */}
        <motion.div className="absolute inset-0" initial={{ rotate: -90 }}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            className="absolute inset-0"
          >
            {/* Background track */}
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              className={colors[theme].progressBg}
              strokeWidth={sizes[size].progressWidth}
              strokeDasharray="1,2"
              strokeLinecap="round"
            />
            {/* Progress indicator */}
            <motion.circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              className={colors[theme].progress}
              strokeWidth={sizes[size].progressWidth}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{
                pathLength: progress / 100,
                opacity: 1,
              }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </svg>
        </motion.div>

        {/* Plate with food */}
        <motion.div
          className={`${sizes[size].plate} bg-gradient-to-br ${colors[theme].light} rounded-full border-4 border-white/80 shadow-lg relative overflow-visible flex items-center justify-center z-10`}
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{
            scale: 1,
            opacity: 1,
            y: 0,
            transition: {
              type: "spring",
              stiffness: 300,
              damping: 20,
            },
          }}
          // animate={{
          //   y: [0, -5, 0],
          //   transition: {
          //     y: {
          //       repeat: Number.POSITIVE_INFINITY,
          //       duration: 2,
          //       ease: "easeInOut",
          //     },
          //   },
          // }}
        >
          {/* Plate inner shadow */}
          <div className="absolute inset-2 rounded-full bg-white/50 shadow-inner" />

          {/* Food item - burger */}
          <motion.div
            className={`${sizes[size].food} relative`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
              rotateZ: [-1, 1, -1],
              transition: {
                scale: { type: "spring", stiffness: 400, damping: 15 },
                opacity: { duration: 0.3 },
                rotateZ: {
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 2,
                  ease: "easeInOut",
                },
              },
            }}
          >
            {/* Burger top bun */}
            <motion.div
              className={`absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b ${colors[theme].secondary} rounded-t-full shadow-md`}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 500 }}
            >
              {/* Sesame seeds */}
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={`seed-${i}`}
                  className="absolute w-1.5 h-2 bg-white/90 rounded-full"
                  style={{
                    top: "20%",
                    left: `${15 + i * 20}%`,
                  }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                />
              ))}
            </motion.div>

            {/* Lettuce */}
            <motion.div
              className="absolute top-1/3 left-0 right-0 h-1/6 bg-green-400 rounded-sm"
              style={{ top: "33%" }}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 500 }}
            />

            {/* Cheese */}
            <motion.div
              className="absolute left-0 right-0 h-1/6 bg-yellow-300 rounded-sm"
              style={{ top: "45%" }}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 500 }}
            />

            {/* Patty */}
            <motion.div
              className={`absolute left-0 right-0 h-1/4 bg-gradient-to-b ${colors[theme].dark} rounded-sm shadow-md`}
              style={{ top: "57%" }}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 500 }}
            />

            {/* Tomato */}
            <motion.div
              className="absolute left-0 right-0 h-1/6 bg-red-500 rounded-sm"
              style={{ top: "75%" }}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5, type: "spring", stiffness: 500 }}
            />

            {/* Bottom bun */}
            <motion.div
              className={`absolute bottom-0 left-0 right-0 h-1/5 bg-gradient-to-b ${colors[theme].secondary} rounded-b-lg shadow-md`}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.6, type: "spring", stiffness: 500 }}
            />
          </motion.div>

          {/* Steam particles */}
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={`steam-${i}`}
                className="absolute w-2 h-2 bg-white rounded-full opacity-70 blur-[1px]"
                initial={{ opacity: 0, y: 0 }}
                animate={{
                  opacity: [0, 0.7, 0],
                  y: [-5, -20],
                  x: [(i - 1) * 5],
                }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: 1.5,
                  delay: i * 0.3,
                }}
              />
            ))}
          </div>
        </motion.div>

        {/* Delivery vehicle */}
        <motion.div
          className={`${sizes[size].vehicle} absolute bottom-1/4 left-1/4`}
          initial={{ x: -100, opacity: 0 }}
          animate={
            isDelivering
              ? {
                  x: [0, 20, 150],
                  y: [0, -10, 0],
                  opacity: [1, 1, 0],
                  transition: {
                    duration: 1.5,
                    times: [0, 0.3, 1],
                    ease: "easeInOut",
                  },
                }
              : {
                  x: 0,
                  opacity: 1,
                  transition: {
                    type: "spring",
                    stiffness: 300,
                    damping: 20,
                  },
                }
          }
        >
          {/* Delivery scooter */}
          <div className="relative w-full h-full">
            {/* Scooter body */}
            <motion.div
              className={`absolute bottom-0 left-1/4 w-1/2 h-1/3 bg-gradient-to-b ${colors[theme].primary} rounded-md shadow-md`}
              animate={{
                y: [0, -1, 0],
              }}
              transition={{
                duration: 0.3,
                repeat: Number.POSITIVE_INFINITY,
                repeatType: "mirror",
              }}
            />

            {/* Front */}
            <motion.div
              className={`absolute bottom-0 left-1/6 w-1/3 h-1/4 bg-gradient-to-b ${colors[theme].dark} rounded-l-full shadow-md`}
              animate={{
                y: [0, -1, 0],
              }}
              transition={{
                duration: 0.3,
                repeat: Number.POSITIVE_INFINITY,
                repeatType: "mirror",
              }}
            />

            {/* Delivery box */}
            <motion.div
              className={`absolute bottom-1/3 right-1/6 w-1/3 h-1/4 bg-gradient-to-b ${colors[theme].secondary} rounded-sm shadow-md`}
              animate={{
                y: [0, -1, 0],
              }}
              transition={{
                duration: 0.3,
                repeat: Number.POSITIVE_INFINITY,
                repeatType: "mirror",
              }}
            >
              {/* Box lid */}
              <motion.div
                className={`absolute -top-1/4 inset-x-0 h-1/4 bg-gradient-to-b ${colors[theme].primary} rounded-t-sm origin-bottom shadow-sm`}
                animate={{
                  rotateX: isDelivering ? [0, 40, 0] : 0,
                }}
                transition={{
                  duration: 0.5,
                  repeat: isDelivering ? 2 : 0,
                  repeatType: "reverse",
                }}
              />
            </motion.div>

            {/* Wheels */}
            <motion.div
              className="absolute bottom-0 left-1/6 w-1/6 h-1/6 bg-gray-800 rounded-full border-2 border-gray-300"
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 1,
                repeat: Number.POSITIVE_INFINITY,
                ease: "linear",
              }}
            />
            <motion.div
              className="absolute bottom-0 right-1/4 w-1/6 h-1/6 bg-gray-800 rounded-full border-2 border-gray-300"
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 1,
                repeat: Number.POSITIVE_INFINITY,
                ease: "linear",
              }}
            />

            {/* Driver */}
            <motion.div
              className={`absolute bottom-1/3 left-1/3 w-1/6 h-1/4 bg-gradient-to-b ${colors[theme].dark} rounded-full shadow-md`}
              animate={{
                y: [0, -1, 0],
              }}
              transition={{
                duration: 0.3,
                repeat: Number.POSITIVE_INFINITY,
                repeatType: "mirror",
              }}
            />
          </div>
        </motion.div>
      </div>

      {/* Loading text with animated dots */}
      <motion.div
        className={`flex items-center justify-center gap-1 mt-4 ${sizes[size].text}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
      >
        <motion.span className={`font-medium ${colors[theme].text}`}>
          {text}
        </motion.span>
        <div className="flex gap-1">
          {[...Array(3)].map((_, i) => (
            <motion.span
              key={`dot-${i}`}
              className={`w-1.5 h-1.5 rounded-full bg-gradient-to-br ${colors[theme].primary}`}
              animate={{
                opacity: [0.3, 1, 0.3],
                y: [0, -3, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 1.2,
                delay: i * 0.2,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
}
